/**
 * AdminUserDetailPage - Detailed view of a single user for super admins
 * Shows user info, company memberships, diagnosis history, AI usage.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import adminService from '../../services/adminService';
import AdminLayout from './AdminLayout';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Content = styled.div`
  animation: ${fadeIn} 0.35s ease-out;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: 24px;
  font-family: ${(p) => p.theme.fonts.main};

  &:hover { color: ${(p) => p.theme.colors.primary}; }
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 32px;
  padding: 24px;
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.bgTertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.primary};
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 4px 0;
`;

const UserEmail = styled.p`
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 0;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.textMuted};
`;

const AdminBadge = styled.span`
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: rgba(236, 72, 153, 0.15);
  color: #EC4899;
  border: 1px solid #EC4899;
  margin-left: 8px;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 16px 0;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const Card = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 640px) { flex-direction: column; align-items: flex-start; }
`;

const CardTitle = styled.div`
  font-weight: 600;
  color: ${(p) => p.theme.colors.text};
  font-size: 0.92rem;
`;

const CardSub = styled.div`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin-top: 2px;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 700;
  ${(p) => {
    switch (p.$role) {
      case 'admin': return `background: rgba(99,102,241,0.15); color: #6366F1; border: 1px solid #6366F1;`;
      case 'manager': return `background: rgba(245,158,11,0.15); color: #F59E0B; border: 1px solid #F59E0B;`;
      default: return `background: ${p.theme.colors.bgTertiary}; color: ${p.theme.colors.textSecondary}; border: 1px solid ${p.theme.colors.border};`;
    }
  }}
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${(p) => p.$status === 'active' ? '#10B981' : '#EF4444'};
`;

const CompanyLink = styled(Link)`
  color: ${(p) => p.theme.colors.primary};
  text-decoration: none;
  font-size: 0.82rem;
  &:hover { text-decoration: underline; }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 32px;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const StatCard = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
  padding: 16px;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: ${(p) => p.theme.colors.text};
  font-family: ${(p) => p.theme.fonts?.mono};
`;

const DiagTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const DiagTh = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.textSecondary};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

const DiagTd = styled.td`
  padding: 10px 12px;
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.text};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
`;

const ProBadge = styled.span`
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 700;
  ${(p) => p.$isPro
    ? `background: rgba(245, 158, 11, 0.15); color: #F59E0B; border: 1px solid #F59E0B;`
    : `background: ${p.theme.colors.bgTertiary}; color: ${p.theme.colors.textSecondary}; border: 1px solid ${p.theme.colors.border};`
  }
`;

const ActionButton = styled.button`
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => p.$variant === 'danger' ? '#EF4444' : p.theme.colors.primary};
  background: ${(p) => p.$variant === 'danger' ? 'rgba(239,68,68,0.1)' : 'rgba(99,102,241,0.1)'};
  color: ${(p) => p.$variant === 'danger' ? '#EF4444' : p.theme.colors.primary};
  font-family: ${(p) => p.theme.fonts.main};

  &:hover {
    background: ${(p) => p.$variant === 'danger' ? 'rgba(239,68,68,0.25)' : 'rgba(99,102,241,0.25)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.82rem;
  margin-bottom: 16px;
  background: rgba(16, 185, 129, 0.1);
  color: #10B981;
  border: 1px solid rgba(16, 185, 129, 0.3);
`;

/* ---- AI chat history ---- */

const ConvRow = styled.button`
  width: 100%;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.$open ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 8px;
  cursor: pointer;
  font-family: ${(p) => p.theme.fonts.main};
  color: inherit;

  &:hover { border-color: ${(p) => p.theme.colors.primary}; }
`;

const ConvTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${(p) => p.theme.colors.text};
`;

const ConvMeta = styled.div`
  font-size: 0.74rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-top: 3px;
`;

const MsgList = styled.div`
  background: ${(p) => p.theme.colors.bgPrimary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 10px;
  padding: 14px;
  margin: -2px 0 14px;
`;

const MsgBubble = styled.div`
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }
`;

const MsgRole = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
  color: ${(p) => p.$role === 'user' ? '#6366F1' : p.theme.colors.primary};
`;

const MsgContent = styled.div`
  font-size: 0.85rem;
  line-height: 1.7;
  color: ${(p) => p.theme.colors.text};
  white-space: pre-wrap;
  word-break: break-word;
`;

const MsgTime = styled.div`
  font-size: 0.68rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-top: 4px;
`;

function AdminUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [openConvId, setOpenConvId] = useState(null);
  const [convDetail, setConvDetail] = useState(null);
  const [convDetailLoading, setConvDetailLoading] = useState(false);

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const fetchUser = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getUserDetail(userId);
      setUser(data);
    } catch (err) {
      setError(err.message || t('ユーザー情報の取得に失敗しました', 'Failed to fetch user'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  const fetchConversations = useCallback(async () => {
    try {
      const data = await adminService.getUserConversations(userId);
      setConversations(Array.isArray(data?.conversations) ? data.conversations : []);
    } catch {
      setConversations([]);
    }
  }, [userId]);

  const toggleConversation = useCallback(async (convId) => {
    if (openConvId === convId) {
      setOpenConvId(null);
      setConvDetail(null);
      return;
    }
    setOpenConvId(convId);
    setConvDetail(null);
    setConvDetailLoading(true);
    try {
      const data = await adminService.getConversationDetail(convId);
      setConvDetail(data);
    } catch {
      setConvDetail(null);
    } finally {
      setConvDetailLoading(false);
    }
  }, [openConvId]);

  const handleGrantPro = async () => {
    if (!window.confirm(t('このユーザーにPro権限を付与しますか？', 'Grant Pro to this user?'))) return;
    setActionLoading(true);
    try {
      const res = await adminService.grantUserPro(userId);
      showMessage(res.message || 'Pro granted');
      fetchUser();
    } catch (err) {
      showMessage(err.message || 'Error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokePro = async () => {
    if (!window.confirm(t('このユーザーのPro権限を取り消しますか？', 'Revoke Pro from this user?'))) return;
    setActionLoading(true);
    try {
      const res = await adminService.revokeUserPro(userId);
      showMessage(res.message || 'Pro revoked');
      fetchUser();
    } catch (err) {
      showMessage(err.message || 'Error');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    fetchConversations();
  }, [fetchUser, fetchConversations]);

  if (loading) {
    return (
      <AdminLayout active="users">
        <Content style={{ textAlign: 'center', padding: 64, color: '#8888a8' }}>
          {t('読み込み中...', 'Loading...')}
        </Content>
      </AdminLayout>
    );
  }

  if (error || !user) {
    return (
      <AdminLayout active="users">
        <Content style={{ textAlign: 'center', padding: 64 }}>
          <p style={{ color: '#EF4444' }}>{error || t('ユーザーが見つかりません', 'User not found')}</p>
        </Content>
      </AdminLayout>
    );
  }

  const initials = (user.name || user.email || '?').charAt(0).toUpperCase();

  return (
    <AdminLayout active="users">
      <Content>
        <BackButton onClick={() => navigate('/admin/users')}>
          ← {t('ユーザー一覧に戻る', 'Back to Users')}
        </BackButton>

        <UserHeader>
          <Avatar>
            {user.avatar_url ? <img src={user.avatar_url} alt="" /> : initials}
          </Avatar>
          <UserInfo>
            <UserName>
              {user.name || t('未設定', 'No name')}
              {user.is_admin && <AdminBadge>ADMIN</AdminBadge>}
            </UserName>
            <UserEmail>{user.email}</UserEmail>
            <MetaRow>
              <MetaItem>ID: {user.id}</MetaItem>
              <MetaItem>{t('登録', 'Registered')}: {user.created_at ? new Date(user.created_at).toLocaleDateString() : '--'}</MetaItem>
              <MetaItem>{t('最終ログイン', 'Last login')}: {user.last_login ? new Date(user.last_login).toLocaleString() : '--'}</MetaItem>
            </MetaRow>
          </UserInfo>
        </UserHeader>

        <StatGrid>
          <StatCard>
            <StatLabel>{t('所属企業数', 'Companies')}</StatLabel>
            <StatValue>{user.companies?.length || 0}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>{t('診断回数', 'Diagnoses')}</StatLabel>
            <StatValue>{user.diagnoses?.length || 0}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>{t('AI利用 (今月)', 'AI Usage (month)')}</StatLabel>
            <StatValue>{user.monthly_ai_usage || 0}</StatValue>
          </StatCard>
        </StatGrid>

        {message && <Message>{message}</Message>}

        {/* Subscription */}
        <Section>
          <SectionTitle>{t('サブスクリプション', 'Subscription')}</SectionTitle>
          <Card>
            <div>
              <CardTitle>
                {t('個人Pro', 'Individual Pro')}
                <span style={{ marginLeft: 8 }}>
                  <ProBadge $isPro={user.subscription_status === 'active'}>
                    {user.subscription_status === 'active' ? 'Pro' : 'Free'}
                  </ProBadge>
                </span>
              </CardTitle>
              {user.subscription_status === 'active' && user.subscription_plan_type && (
                <CardSub>{t('プラン', 'Plan')}: {user.subscription_plan_type}</CardSub>
              )}
            </div>
            <div>
              {user.subscription_status !== 'active' ? (
                <ActionButton onClick={handleGrantPro} disabled={actionLoading}>
                  {t('Pro付与', 'Grant Pro')}
                </ActionButton>
              ) : (
                <ActionButton $variant="danger" onClick={handleRevokePro} disabled={actionLoading}>
                  {t('Pro取消', 'Revoke Pro')}
                </ActionButton>
              )}
            </div>
          </Card>
        </Section>

        {/* Company memberships */}
        <Section>
          <SectionTitle>{t('所属企業', 'Company Memberships')}</SectionTitle>
          {(!user.companies || user.companies.length === 0) ? (
            <Card>
              <CardSub>{t('企業に所属していません', 'No company memberships')}</CardSub>
            </Card>
          ) : (
            user.companies.map((c, i) => (
              <Card key={i}>
                <div>
                  <CardTitle>{c.company_name}</CardTitle>
                  <CardSub>
                    <StatusDot $status={c.status} />{c.status}
                    {c.type_code && <span style={{ marginLeft: 12 }}>Type: {c.type_code}</span>}
                    {c.joined_at && <span style={{ marginLeft: 12 }}>{new Date(c.joined_at).toLocaleDateString()}</span>}
                  </CardSub>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <RoleBadge $role={c.role}>{c.role}</RoleBadge>
                  {c.company_slug && (
                    <CompanyLink to={`/company/${c.company_slug}`}>
                      {t('開く', 'Open')} →
                    </CompanyLink>
                  )}
                </div>
              </Card>
            ))
          )}
        </Section>

        {/* Diagnosis history */}
        <Section>
          <SectionTitle>{t('診断履歴', 'Diagnosis History')}</SectionTitle>
          {(!user.diagnoses || user.diagnoses.length === 0) ? (
            <Card>
              <CardSub>{t('診断履歴がありません', 'No diagnosis history')}</CardSub>
            </Card>
          ) : (
            <TableWrapper>
              <DiagTable>
                <thead>
                  <tr>
                    <DiagTh>{t('ネイタル (本来)', 'Natal')}</DiagTh>
                    <DiagTh>{t('カレント (現在)', 'Current')}</DiagTh>
                    <DiagTh>{t('コード', 'Code')}</DiagTh>
                    <DiagTh>{t('種別', 'Kind')}</DiagTh>
                    <DiagTh>{t('日時', 'Date')}</DiagTh>
                  </tr>
                </thead>
                <tbody>
                  {user.diagnoses.map((d, i) => (
                    <tr key={i}>
                      <DiagTd style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                        {d.natal_type_code || d.type_code || '--'}
                        {d.natal_type_label && (
                          <span style={{ color: '#8888a8', fontWeight: 400, marginLeft: 6 }}>
                            {d.natal_type_label}
                          </span>
                        )}
                      </DiagTd>
                      <DiagTd style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                        {d.current_type_code || '--'}
                        {d.current_type_label && (
                          <span style={{ color: '#8888a8', fontWeight: 400, marginLeft: 6 }}>
                            {d.current_type_label}
                          </span>
                        )}
                      </DiagTd>
                      <DiagTd style={{ fontSize: '0.74rem', fontFamily: "'JetBrains Mono', monospace", color: '#8888a8' }}>
                        {d.current_struct_code || d.natal_struct_code || d.struct_code || '--'}
                      </DiagTd>
                      <DiagTd>{d.diagnosis_type || '--'}</DiagTd>
                      <DiagTd style={{ fontSize: '0.82rem', color: '#8888a8' }}>
                        {d.created_at ? new Date(d.created_at).toLocaleString() : '--'}
                      </DiagTd>
                    </tr>
                  ))}
                </tbody>
              </DiagTable>
            </TableWrapper>
          )}
        </Section>

        {/* AI chat history */}
        <Section>
          <SectionTitle>{t('AIチャット履歴', 'AI Chat History')}</SectionTitle>
          {conversations.length === 0 ? (
            <Card>
              <CardSub>{t('チャット履歴がありません', 'No chat history')}</CardSub>
            </Card>
          ) : (
            conversations.map((c) => (
              <div key={c.id}>
                <ConvRow $open={openConvId === c.id} onClick={() => toggleConversation(c.id)}>
                  <div style={{ minWidth: 0 }}>
                    <ConvTitle>{c.title || t('(無題)', '(untitled)')}</ConvTitle>
                    <ConvMeta>
                      {c.message_count || 0} {t('メッセージ', 'messages')}
                      {' · '}
                      {c.updated_at ? new Date(c.updated_at).toLocaleString() : '--'}
                    </ConvMeta>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#8888a8', fontSize: '0.8rem' }}>
                      {openConvId === c.id ? '▲' : '▼'}
                    </span>
                  </div>
                </ConvRow>
                {openConvId === c.id && (
                  <MsgList>
                    {convDetailLoading ? (
                      <CardSub>{t('読み込み中...', 'Loading...')}</CardSub>
                    ) : convDetail && convDetail.messages?.length > 0 ? (
                      convDetail.messages.map((m, mi) => (
                        <MsgBubble key={mi}>
                          <MsgRole $role={m.role}>
                            {m.role === 'user' ? t('ユーザー', 'User') : 'AI'}
                          </MsgRole>
                          <MsgContent>{m.content}</MsgContent>
                          {m.created_at && (
                            <MsgTime>{new Date(m.created_at).toLocaleString()}</MsgTime>
                          )}
                        </MsgBubble>
                      ))
                    ) : (
                      <CardSub>{t('メッセージがありません', 'No messages')}</CardSub>
                    )}
                  </MsgList>
                )}
              </div>
            ))
          )}
        </Section>
      </Content>
    </AdminLayout>
  );
}

export default AdminUserDetailPage;
